type NavItem = {
  href: string;
  label: string;
};

type Feature = {
  title: string;
  description: string;
  accent: "openai" | "anthropic" | "gemini" | "neutral";
  stat: string;
};

type ComparisonRow = {
  label: string;
  ours: string;
  typical: string;
};

type UseCase = {
  title: string;
  description: string;
};

type Faq = {
  question: string;
  answer: string;
};

const navItems: NavItem[] = [
  { href: "#features", label: "Features" },
  { href: "./privacy.html", label: "Privacy" },
  { href: "#pricing", label: "Pricing" },
  { href: "#faq", label: "FAQ" },
];

const legalLinks = [
  { href: "./privacy.html", label: "Privacy Policy" },
  { href: "./terms.html", label: "Terms" },
];

const features: Feature[] = [
  {
    title: "Highlight-to-ask anywhere",
    description:
      "Select text on any page and launch rewrite, translate, summarize, or custom prompt flows without leaving the tab.",
    accent: "openai",
    stat: "Selection popup",
  },
  {
    title: "Multi-model side panel",
    description:
      "Switch between OpenAI, Anthropic, Gemini, DeepSeek, Qwen, MiniMax, and Kimi from one persistent browser side panel with local chat history.",
    accent: "gemini",
    stat: "Model-agnostic",
  },
  {
    title: "Page and YouTube context",
    description:
      "Pull cleaned page text or transcripts into the prompt pipeline so the model sees the page you are actually working on.",
    accent: "anthropic",
    stat: "Context-aware",
  },
  {
    title: "Slash templates and workflows",
    description:
      "Create reusable commands like /brief, /sales-email, or /bug-report and run them against selections or full-page context.",
    accent: "neutral",
    stat: "Prompt shortcuts",
  },
  {
    title: "Network transparency log",
    description:
      "Inspect every outbound request, destination, and byte count so privacy claims are visible instead of hand-wavy.",
    accent: "gemini",
    stat: "Auditable",
  },
  {
    title: "No prompt relay",
    description:
      "The extension runs locally, stores settings locally, and sends prompts directly from the browser to the provider you choose.",
    accent: "openai",
    stat: "Zero relay",
  },
];

const comparisonRows: ComparisonRow[] = [
  {
    label: "Prompt traffic",
    ours: "Browser to provider API only",
    typical: "Routed through vendor backend",
  },
  {
    label: "Business model",
    ours: "One-time product + your API bill",
    typical: "Recurring subscription markup",
  },
  {
    label: "Model choice",
    ours: "OpenAI, Anthropic, Gemini, DeepSeek, Qwen, MiniMax, Kimi",
    typical: "Usually locked to one stack",
  },
  {
    label: "Verification",
    ours: "Network log built into the UI",
    typical: "Trust the marketing page",
  },
];

const useCases: UseCase[] = [
  {
    title: "Agencies",
    description:
      "Use approved corporate keys instead of copying client material into consumer chat apps.",
  },
  {
    title: "Analysts",
    description:
      "Summarize long pages, compare provider outputs, and keep the model alongside the source material.",
  },
  {
    title: "Developers",
    description:
      "Get inline rewrite, debugging, and drafting help without shipping your workflow to a mystery SaaS relay.",
  },
];

const faqs: Faq[] = [
  {
    question: "What is BringYourOwnAI?",
    answer:
      "BringYourOwnAI is a Chrome browser extension that connects OpenAI, Anthropic, Gemini, DeepSeek, Qwen, MiniMax, and Kimi to any webpage using your own API keys. It provides a highlight-to-ask selection popup, a persistent AI side panel with local chat history, page and YouTube summarization, and slash prompt templates — with no relay server and no telemetry.",
  },
  {
    question: "How is BringYourOwnAI different from other AI browser extensions?",
    answer:
      "Most AI browser extensions route your prompts through the vendor's own backend. BringYourOwnAI removes that relay layer entirely — the extension sends requests directly from your browser to the provider you choose using your own API keys. There is no subscription markup on top of your provider bill, no telemetry, and a built-in network log you can audit in real time.",
  },
  {
    question: "Is there a free trial?",
    answer:
      "Yes. BringYourOwnAI includes a 2-day free trial. No credit card is required to start the trial. After the trial period you can purchase lifetime access for a one-time fee.",
  },
  {
    question: "How much does BringYourOwnAI cost?",
    answer:
      "BringYourOwnAI is planned as a one-time purchase at $39.99 — no recurring subscription. You pay your own AI provider directly for API usage, which means no markup and full cost visibility.",
  },
  {
    question: "If there is a website, how can the product still have no backend?",
    answer:
      "The site hosts marketing content and docs, and the extension uses a lightweight licensing endpoint for trial and purchase verification. Prompts and page data still go directly to the AI provider you choose — never through a BringYourOwnAI relay.",
  },
  {
    question: "Where are API keys stored?",
    answer:
      "Encrypted at rest in a local vault stored in your browser's extension storage. The vault passphrase exists only for the current browser session and is never transmitted to BringYourOwnAI servers. Setting provider-side spend limits is still recommended.",
  },
  {
    question: "Who is BringYourOwnAI for?",
    answer:
      "Developers, analysts, and agency professionals who already have API keys with one or more major AI providers and want browser-native AI tooling without adding another subscription layer or vendor relay.",
  },
];

const architectureSteps = [
  "Page selections and page context are captured locally",
  "The extension prepares the provider request on-device",
  "Prompts go directly to the chosen provider",
  "Network log records every outbound call in plain view",
];

const providerSwatches = [
  { label: "OpenAI", accent: "openai" as const },
  { label: "Anthropic", accent: "anthropic" as const },
  { label: "Gemini", accent: "gemini" as const },
  { label: "DeepSeek", accent: "neutral" as const },
  { label: "Qwen", accent: "neutral" as const },
  { label: "MiniMax", accent: "neutral" as const },
  { label: "Kimi", accent: "neutral" as const },
];

function ProviderPill({
  accent,
  label,
}: {
  accent: "openai" | "anthropic" | "gemini" | "neutral";
  label: string;
}) {
  return (
    <span className={`provider-pill provider-pill--${accent}`}>{label}</span>
  );
}

function FeatureCard({ accent, description, stat, title }: Feature) {
  return (
    <article className={`feature-card feature-card--${accent}`}>
      <div className="feature-card__eyebrow">{stat}</div>
      <h3>{title}</h3>
      <p>{description}</p>
    </article>
  );
}

function ComparisonTable() {
  return (
    <div className="comparison-card">
      <div className="comparison-card__header">
        <span className="section-label">Operating model</span>
        <h3>No relay. No telemetry theatre.</h3>
        <p>
          BringYourOwnAI is designed to keep the runtime boring in exactly the
          way privacy-sensitive users want.
        </p>
      </div>

      <div className="comparison-table" role="table" aria-label="Comparison">
        <div className="comparison-table__row comparison-table__row--head" role="row">
          <span role="columnheader">Category</span>
          <span role="columnheader">BringYourOwnAI</span>
          <span role="columnheader">Typical AI extension</span>
        </div>
        {comparisonRows.map((row) => (
          <div className="comparison-table__row" key={row.label} role="row">
            <span role="cell">{row.label}</span>
            <span role="cell">{row.ours}</span>
            <span role="cell">{row.typical}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ArchitectureRail() {
  return (
    <div className="rail-card">
      <div className="rail-card__header">
        <span className="section-label">Architecture</span>
        <h3>Privacy claims that line up with the packet flow.</h3>
      </div>

      <div className="architecture-stack">
        <div className="node node--surface">
          <span className="node__label">Browser runtime</span>
          <strong>Selection popup, side panel, templates</strong>
        </div>
        <div className="node node--path" aria-hidden="true" />
        <div className="node node--surface">
          <span className="node__label">Local extension runtime</span>
          <strong>Provider switching, request logging, tab coordination</strong>
        </div>
        <div className="node node--path" aria-hidden="true" />
        <div className="node node--provider">
          <span className="node__label">Provider API</span>
          <strong>OpenAI, Anthropic, Gemini, DeepSeek, Qwen, MiniMax, or Kimi</strong>
        </div>
      </div>

      <div className="warning-card">
        <span className="section-label">Important</span>
        <p>
          Saved API keys and any saved license key are encrypted at rest in a
          local vault. The unlock passphrase stays only in the current browser
          session.
        </p>
      </div>
    </div>
  );
}

function NetworkLogPreview() {
  return (
    <div className="terminal-card">
      <div className="terminal-card__toolbar">
        <span />
        <span />
        <span />
      </div>
      <pre>
        <code>{`[Network Log]
POST api.openai.com/v1/chat/completions      18.4 KB
POST api.deepseek.com/chat/completions       11.2 KB
POST api.moonshot.ai/v1/chat/completions     13.8 KB
POST bringyourownai.vwapster.cloud/api/...    0.8 KB

[Storage]
chrome.storage.local
  secureVault                  encrypted
chrome.storage.session
  secureVaultSession           present

[Mode]
telemetry          disabled
relay server       none
license checks     enabled
analytics          none`}</code>
      </pre>
    </div>
  );
}

export function LandingPage() {
  return (
    <div className="landing-page">
      <header className="topbar">
        <div className="container topbar__inner">
          <a className="brand" href="#top">
            <span className="brand__mark">BYO//AI</span>
            <span className="brand__text">BringYourOwnAI</span>
          </a>

          <nav className="topbar__nav" aria-label="Primary">
            {navItems.map((item) => (
              <a key={item.href} href={item.href}>
                {item.label}
              </a>
            ))}
          </nav>

          <a className="button button--ghost" href="#privacy">
            Read the privacy model
          </a>
        </div>
      </header>

      <main id="top">
        <section className="hero">
          <div className="hero__grid" aria-hidden="true" />
          <div className="container hero__inner">
            <div className="hero__copy">
              <span className="section-label section-label--hero">
                Zero-telemetry Chrome AI extension
              </span>
              <h1>
                Your keys. Your AI. No relay.
              </h1>
              <p className="hero__lede">
                BringYourOwnAI is a Chrome extension that connects OpenAI,
                Anthropic, Gemini, DeepSeek, Qwen, MiniMax, and Kimi to any
                webpage using your own API keys. The extension runs locally,
                stores settings on your device, and sends prompts directly from
                your browser to the provider you choose. No middleman. A
                lightweight licensing check handles the 2-day free trial and
                one-time lifetime access.
              </p>

              <div className="hero__actions">
                <a className="button button--light" href="#pricing">
                  See pricing — $39.99 one-time
                </a>
                <a className="button button--dark" href="#features">
                  See what ships in the extension
                </a>
              </div>

              <div className="hero__providers">
                {providerSwatches.map((provider) => (
                  <ProviderPill
                    key={provider.label}
                    accent={provider.accent}
                    label={provider.label}
                  />
                ))}
                <span className="meta-pill">2-day free trial</span>
                <span className="meta-pill">No credit card required</span>
                <span className="meta-pill">Encrypted local vault</span>
              </div>
            </div>

            <div className="hero__panel">
              <div className="signal-card">
                <div className="signal-card__header">
                  <span className="section-label">Positioning</span>
                  <strong>Sovereignty-first AI for the browser</strong>
                </div>
                <ul className="signal-list">
                  <li>Your keys stay yours.</li>
                  <li>Your prompt path stays inspectable.</li>
                  <li>Your model choice stays flexible.</li>
                </ul>
              </div>

              <NetworkLogPreview />
            </div>
          </div>
        </section>

        <section className="section section--light" id="features">
          <div className="container">
            <div className="section-heading">
              <span className="section-label">Core product</span>
              <h2>What BringYourOwnAI does — and what it deliberately does not do.</h2>
              <p>
                The workflow is intentionally simple: highlight text on any
                page, open the AI side panel, bring your own provider API key,
                and keep the entire data path visible in the built-in network
                log.
              </p>
            </div>

            <div className="features-grid">
              {features.map((feature) => (
                <FeatureCard key={feature.title} {...feature} />
              ))}
            </div>
          </div>
        </section>

        <section className="section section--split" id="privacy">
          <div className="container split-layout">
            <ArchitectureRail />
            <ComparisonTable />
          </div>
        </section>

        <section className="section section--light">
          <div className="container">
            <div className="section-heading section-heading--compact">
              <span className="section-label">Workflow fit</span>
              <h2>Built for people who already know what an API key is worth.</h2>
            </div>

            <div className="use-case-grid">
              {useCases.map((useCase) => (
                <article className="use-case-card" key={useCase.title}>
                  <h3>{useCase.title}</h3>
                  <p>{useCase.description}</p>
                </article>
              ))}
            </div>

            <div className="steps-panel">
              <div>
                <span className="section-label">Runtime flow</span>
                <h3>How the privacy model actually behaves in practice</h3>
              </div>
              <ol className="steps-list">
                {architectureSteps.map((step) => (
                  <li key={step}>{step}</li>
                ))}
              </ol>
            </div>
          </div>
        </section>

        <section className="section section--dark" id="pricing">
          <div className="container pricing-layout">
            <div className="pricing-copy">
              <span className="section-label section-label--hero">Pricing posture</span>
              <h2>One-time product economics. Your provider bill stays with your provider.</h2>
              <p>
                The point of BringYourOwnAI is not to become another monthly AI
                tax. The extension is designed around BYOK so users pay for the
                software once and keep direct control over model spend.
              </p>
            </div>

            <div className="pricing-card">
              <div className="pricing-card__tag">Lifetime access — one-time purchase</div>
              <div className="pricing-card__price">
                <span>$39.99</span>
                <small>one-time</small>
              </div>
              <p>
                Use your own provider account. No recurring platform markup.
                Includes a 2-day free trial — no credit card required to start.
              </p>
              <a
                className="button button--light button--full"
                href="https://claudia-ai.lemonsqueezy.com/checkout/buy/af04459f-089b-43a7-b74a-d51e41e1d9fe"
              >
                Buy lifetime access
              </a>
            </div>
          </div>
        </section>

        <section className="section section--light" id="faq">
          <div className="container">
            <div className="section-heading section-heading--compact">
              <span className="section-label">FAQ</span>
              <h2>Questions people should ask before they trust a browser AI tool.</h2>
            </div>

            <div className="faq-list">
              {faqs.map((faq) => (
                <details className="faq-item" key={faq.question}>
                  <summary>{faq.question}</summary>
                  <p>{faq.answer}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>

      <footer className="footer">
        <div className="container footer__inner">
          <div>
            <span className="brand__mark">BYO//AI</span>
            <p>
              BringYourOwnAI is a zero-telemetry Chrome extension for users who
              want local-first UX and direct provider access.
            </p>
          </div>
          <div className="footer__links">
            {navItems.map((item) => (
              <a key={item.href} href={item.href}>
                {item.label}
              </a>
            ))}
            {legalLinks.map((item) => (
              <a key={item.href} href={item.href}>
                {item.label}
              </a>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
}
